<template>
  <Modal ref="modalRef" width="27.5rem" @after-close="reset">
    <template #header>
      <Text variant="h4">Turn off two-factor?</Text>
    </template>

    <template #body>
      <View direction="column" gap="md">
        <Text variant="body-medium" color="secondary">Enter the code from your authenticator app to confirm.</Text>
        <FormField :error="codeError ? [codeError] : undefined">
          <TwoFactorCodeField ref="codeFieldRef" v-model="code" :disabled="isDisabling" @complete="disable" />
        </FormField>
      </View>
    </template>

    <template #footer>
      <Button variant="ghost" size="medium" @click="close">Cancel</Button>
      <Button size="medium" appearance="danger" :disabled="code.length < 6" :loading="isDisabling" @click="disable">
        Turn off
      </Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { TwoFactorCodeField } from '@tallo/auth';
import { Button, extractErrorMessage, FormField, Modal, Text, useAlertsStore, View } from '@tallo/design-system';
import { profileService, useInvestorStore } from '@tallo/investor/investor';

const investorStore = useInvestorStore();
const alertsStore = useAlertsStore();

const modalRef = ref<InstanceType<typeof Modal>>();
const codeFieldRef = ref<InstanceType<typeof TwoFactorCodeField>>();
const code = ref('');
const codeError = ref('');
const isDisabling = ref(false);

function show() {
  modalRef.value?.show();
  codeFieldRef.value?.focus();
}

function close() {
  modalRef.value?.close();
}

function reset() {
  code.value = '';
  codeError.value = '';
}

async function disable() {
  if (isDisabling.value) return;

  isDisabling.value = true;
  codeError.value = '';

  try {
    await profileService.disableTwoFactor(code.value.trim());
    await investorStore.load();
    alertsStore.success('Two-factor authentication is off');
    close();
  } catch (error) {
    codeError.value = extractErrorMessage(error, 'Invalid code. Please try again.');
  } finally {
    isDisabling.value = false;
  }
}

defineExpose({ show });
</script>
